/**
 * Period model for the LLM cost card — month / quarter / custom ranges as
 * inclusive YYYY-MM-DD strings (the wire format of /api/insights/cost/).
 * Pure functions, dayjs only, so the stepper logic is unit-testable.
 */

import dayjs, { type Dayjs } from 'dayjs';

export type PeriodMode = 'month' | 'quarter' | 'custom';

export interface Period {
  mode: PeriodMode;
  from: string; // inclusive
  to: string; // inclusive
  label: string;
}

const ISO = 'YYYY-MM-DD';

function monthPeriodOf(d: Dayjs): Period {
  const start = d.startOf('month');
  return {
    mode: 'month',
    from: start.format(ISO),
    to: start.endOf('month').format(ISO),
    label: start.format('MMM YYYY'),
  };
}

function quarterPeriodOf(d: Dayjs): Period {
  const q = Math.floor(d.month() / 3); // 0-3
  const start = d.startOf('year').add(q * 3, 'month');
  return {
    mode: 'quarter',
    from: start.format(ISO),
    to: start.add(2, 'month').endOf('month').format(ISO),
    label: `Q${q + 1} ${start.year()}`,
  };
}

export function currentMonthPeriod(now: Date = new Date()): Period {
  return monthPeriodOf(dayjs(now));
}

export function currentQuarterPeriod(now: Date = new Date()): Period {
  return quarterPeriodOf(dayjs(now));
}

export function customPeriod(from: string, to: string): Period {
  const f = dayjs(from);
  const t = dayjs(to);
  const label =
    f.year() === t.year()
      ? `${f.format('MMM D')} – ${t.format('MMM D')}`
      : `${f.format('MMM D, YYYY')} – ${t.format('MMM D, YYYY')}`;
  return { mode: 'custom', from, to, label };
}

export function stepPeriod(p: Period, dir: 1 | -1): Period {
  if (p.mode === 'month') return monthPeriodOf(dayjs(p.from).add(dir, 'month'));
  if (p.mode === 'quarter') return quarterPeriodOf(dayjs(p.from).add(dir * 3, 'month'));
  return p; // custom periods don't step
}

export function isCurrentPeriod(p: Period, now: Date = new Date()): boolean {
  if (p.mode === 'month') return currentMonthPeriod(now).from === p.from;
  if (p.mode === 'quarter') return currentQuarterPeriod(now).from === p.from;
  return false;
}
